import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import Typography from '@mui/material/Typography'
import { alpha } from '@mui/material/styles'
import dayjs from 'dayjs'
import 'dayjs/locale/pt-br'
import 'dayjs/locale/en'
import 'dayjs/locale/es'
import { BellRing, Check, ClipboardList, Flag, MapPin } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAvisosPendentes, useDarCiente, type AvisoDeProgramacao } from '@/features/portal/programacao/api'
import { ChipDeHorario } from '@/features/portal/programacao/ChipDeHorario'
import { ChipDoStatus } from '@/features/portal/programacao/comum'
import { diferenca } from '@/features/portal/programacao/programacaoLogica'
import { useI18n } from '@/core/i18n/useI18n'
import { useToast } from '../feedback/toast'
import { StatusChip } from '../components/StatusChip'
import { apiErrorMessage } from '../apiError'
import { brand, EASE_ORGANIC, LEAF_RADIUS } from '../../styles/theme'

const LOCALE = { pt: 'pt-br', en: 'en', es: 'es' } as const

/**
 * Programação do cliente chegou: aviso em tela cheia para a dupla do contrato
 * (03/11/2026). Pedido do Thiago: "toda hora que ele atualizar a página, aparece
 * uma mensagem na tela de todo tamanho". O aviso só some quando alguém da dupla dá
 * ciente, e o ciente vale para a dupla inteira.
 *
 * Desde 03/11/2026 o aviso leva à programação no módulo Programação do cliente, e
 * não mais à Frente: nenhuma linha nasce sozinha, a programadora gera as que quiser.
 */
export function AvisoProgramacao() {
  const { t } = useI18n()
  const avisos = useAvisosPendentes(true)
  const ciente = useDarCiente()
  const toast = useToast()
  const navigate = useNavigate()
  const lista = avisos.data ?? []

  if (lista.length === 0) return null

  const titulo = lista.length > 1
    ? t('layout.avisoProgramacao.varias', { n: lista.length })
    : t(`layout.avisoProgramacao.titulo.${lista[0].tipo}`)

  async function darCiente(uuids: string[]) {
    try {
      for (const u of uuids) await ciente.mutateAsync(u)
    } catch (err) {
      toast.error(apiErrorMessage(err))
    }
  }

  return (
    <Dialog
      open
      fullScreen
      PaperProps={{ sx: { bgcolor: 'background.default', backgroundImage: 'none' } }}
      aria-labelledby="aviso-programacao-titulo"
    >
      <Box sx={{ minHeight: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', px: { xs: 2, md: 4 }, py: { xs: 4, md: 7 } }}>
        <Box sx={{ position: 'relative', width: 96, height: 96, mb: 3, display: 'grid', placeItems: 'center' }}>
          <Box sx={{ position: 'absolute', inset: 0, borderRadius: '50%', bgcolor: alpha(brand.folha, 0.22), animation: 'pulso 1.8s ease-out infinite', '@keyframes pulso': { '0%': { transform: 'scale(0.85)', opacity: 0.9 }, '100%': { transform: 'scale(1.45)', opacity: 0 } } }} />
          <Box sx={{ position: 'relative', width: 84, height: 84, borderRadius: '50%', bgcolor: brand.floresta, color: '#fff', display: 'grid', placeItems: 'center', animation: 'sino 1.6s ease-in-out infinite', '@keyframes sino': { '0%, 70%, 100%': { transform: 'rotate(0)' }, '78%': { transform: 'rotate(-12deg)' }, '86%': { transform: 'rotate(10deg)' }, '94%': { transform: 'rotate(-6deg)' } } }}>
            <BellRing size={40} />
          </Box>
        </Box>
        <Typography id="aviso-programacao-titulo" component="h1" sx={{ fontFamily: '"Fredoka", sans-serif', fontWeight: 600, fontSize: { xs: '2rem', md: '2.75rem' }, color: brand.floresta, textAlign: 'center', lineHeight: 1.15 }}>
          {titulo}
        </Typography>
        <Typography sx={{ mt: 1, mb: 4, color: 'text.secondary', textAlign: 'center', maxWidth: 560 }}>
          {t('layout.avisoProgramacao.subtitulo')}
        </Typography>

        <Box className="anim-stagger" sx={{ width: '100%', maxWidth: 760, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {lista.map((a) => (
            <CardDoAviso key={a.uuid} aviso={a} onCiente={() => void darCiente([a.uuid])} desabilitado={ciente.isPending}
              onAbrir={() => { void darCiente([a.uuid]); navigate(a.programacao_uuid ? `/logistica/programacao-cliente/${a.programacao_uuid}` : '/logistica/programacao-cliente') }} />
          ))}
        </Box>

        <Box sx={{ mt: 4, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1.5 }}>
          <Button variant="outlined" size="large" startIcon={<ClipboardList size={18} />} sx={{ borderRadius: '12px', fontWeight: 800 }}
            onClick={() => { void darCiente(lista.map((a) => a.uuid)); navigate('/logistica/programacao-cliente') }}>
            {t('layout.avisoProgramacao.abrirModulo')}
          </Button>
          {lista.length > 1 && (
            <Button variant="contained" size="large" startIcon={<Check size={18} />} disabled={ciente.isPending} sx={{ borderRadius: '12px', fontWeight: 800 }}
              onClick={() => void darCiente(lista.map((a) => a.uuid))}>
              {t('layout.avisoProgramacao.cienteTodas')}
            </Button>
          )}
        </Box>
      </Box>
    </Dialog>
  )
}

function CardDoAviso({ aviso, onCiente, onAbrir, desabilitado }: {
  aviso: AvisoDeProgramacao
  onCiente: () => void
  onAbrir: () => void
  desabilitado: boolean
}) {
  const { t, lang } = useI18n()
  const r = aviso.resumo
  const reprog = aviso.tipo !== 'nova'
  const cancelamento = aviso.tipo === 'cancelamento'
  // A mudança feita na prévia fica só no histórico (Samuel, 04/11/2026): o aviso
  // mostra o antes e o agora, sem chamar de carga extra nem de redução.
  const conta = r.conta !== false
  const depois = r.depois.filter((h): h is string => Boolean(h))
  const antes = (r.antes ?? []).filter((h): h is string => Boolean(h))
  const diff = diferenca(antes, depois)
  const dia = r.data ? dayjs(r.data).locale(LOCALE[lang]).format('dddd, DD/MM') : ''
  const qtd = (n: number) => (n === 1 ? t('layout.avisoProgramacao.umaCarga') : t('layout.avisoProgramacao.cargas', { n }))

  return (
    <Card sx={{ p: { xs: 2.25, md: 3 }, borderRadius: LEAF_RADIUS, transition: `transform .2s ${EASE_ORGANIC}` }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
            <Chip size="small" label={t(`layout.avisoProgramacao.tipo.${aviso.tipo}`)}
              sx={{
                fontWeight: 800,
                bgcolor: cancelamento ? alpha('#dc2626', 0.12) : reprog ? alpha(brand.folha, 0.2) : alpha(brand.floresta, 0.12),
                color: cancelamento ? '#991b1b' : reprog ? brand.folhaDeep : brand.floresta,
              }} />
            {r.status && <ChipDoStatus status={r.status} />}
            {aviso.dupla ? <Chip size="small" variant="outlined" label={aviso.dupla} /> : <Chip size="small" color="warning" label={t('layout.avisoProgramacao.semDupla')} />}
          </Box>
          <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 800, lineHeight: 1.4, display: 'block' }}>{r.cliente}</Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2 }}>{r.contrato.numero}</Typography>
          {r.contrato.produtor && <Typography variant="body2" sx={{ color: 'text.secondary' }}>{r.contrato.produtor}</Typography>}
        </Box>
        <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
          <Typography sx={{ fontFamily: '"Fredoka", sans-serif', fontSize: '1.5rem', fontWeight: 600, color: brand.floresta, '&::first-letter': { textTransform: 'uppercase' } }}>{dia}</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('layout.avisoProgramacao.por', { quem: r.quem })}</Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
        {([[<MapPin key="f" size={16} />, 'fazenda', r.contrato.fazenda], [<Flag key="d" size={16} />, 'destino', r.contrato.destino]] as const).map(([icone, chave, valor]) => (
          <Box key={chave} sx={{ display: 'flex', gap: 1, minWidth: 0 }}>
            <Box sx={{ color: brand.floresta, mt: '2px', display: 'flex' }}>{icone}</Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.2 }}>{t(`layout.avisoProgramacao.${chave}`)}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{valor ?? ''}</Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {reprog && r.antes ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          {([['antes', antes, diff.saiu], ['agora', depois, diff.entrou]] as const).map(([chave, lista, marcados]) => (
            <Box key={chave}>
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 800, mb: 0.75 }}>
                {t(`layout.avisoProgramacao.${chave}`)}
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>{qtd(lista.length)}</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {lista.map((h, i) => <ChipDeHorario key={`${chave}-${i}`} horario={h} destaque={marcados.has(h)} />)}
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>{qtd(depois.length)}</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {depois.map((h, i) => <ChipDeHorario key={i} horario={h} />)}
          </Box>
        </Box>
      )}

      {reprog && conta && ((r.extras ?? 0) > 0 || (r.reducoes ?? 0) > 0) && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
          {(r.extras ?? 0) > 0 && <StatusChip label={t('layout.avisoProgramacao.extras', { n: r.extras ?? 0 })} tone="success" />}
          {(r.reducoes ?? 0) > 0 && <StatusChip label={t('layout.avisoProgramacao.reducoes', { n: r.reducoes ?? 0 })} tone="error" />}
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1.5, mt: 2.5 }}>
        <Button variant="outlined" startIcon={<ClipboardList size={18} />} onClick={onAbrir} disabled={desabilitado} sx={{ borderRadius: '12px', fontWeight: 800 }}>
          {t('layout.avisoProgramacao.abrir')}
        </Button>
        <Button variant="contained" startIcon={<Check size={18} />} onClick={onCiente} disabled={desabilitado} sx={{ borderRadius: '12px', fontWeight: 800, px: 3 }}>
          {t('layout.avisoProgramacao.ciente')}
        </Button>
      </Box>
    </Card>
  )
}
